import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useUniqueId } from "../../common/hooks";
import { requiredIf } from "../../common/proptypes";
import { getFocusableElements } from "../../common/utilities";
import { Container } from "../..";
import { DISCLOSURE_LISTS_SPACER, LEFT_SECTION_FOCUSABLE_SELECTORS } from "./constants";
import { DisclosureListContainerContext } from "./DisclosureListContainerContext";
import extractTheme from "./extractTheme";
/**
 * Wrap the DisclosureList children as list items in an unordered list.
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";

function EnhancedDisclosureLists(_ref) {
  let {
    children
  } = _ref;

  // Don't wrap a single DisclosureList in a ul.
  if (React.Children.count(children) === 1) {
    return children;
  } else {
    return _jsx("ul", {
      className: "wf-list-unstyled",
      children: React.Children.map(children, child => {
        return _jsx("li", {
          children: child
        });
      })
    });
  }
}
/**
 * DisclosureListContainer is a React component that provides a container for
 * a set of DisclosureLists.
 */


function DisclosureListContainer(_ref2) {
  let {
    title,
    header,
    children,
    height,
    leftColumnWidth,
    spacing,
    width,
    ["aria-label"]: ariaLabel
  } = _ref2;
  const theme = useTheme();
  let ctx = useContext(DisclosureListContainerContext);
  const [content, setContent] = useState();
  const [contentAriaLabel, setContentAriaLabel] = useState();
  const [activeDisclosureListId, setActiveDisclosureListId] = useState();
  const [disclosureListsHeight, setDisclosureListsHeight] = useState(0);
  const headerRef = useRef();
  const leftColumnRef = useRef();
  const containerRef = useRef();
  const {
    cssRoot,
    cssInnerContainer,
    cssHeader,
    cssDisclosureLists,
    cssLeftColumn,
    cssRightColumn,
    cssContentArea
  } = extractTheme({
    theme,
    height,
    disclosureListsHeight,
    leftColumnWidth,
    width,
    header
  });
  const contentContainerId = useUniqueId("pnr-disclosure-list-content-");
  /**
   * Set content and content aria-label from child DisclosureLists
   * using `onContentChange` callback.
   */

  const handleContentChange = (content, contentAriaLabel, disclosureListId) => {
    setContent(content);
    setContentAriaLabel(contentAriaLabel);
    setActiveDisclosureListId(disclosureListId);
  };

  ctx = {
    onContentChange: handleContentChange,
    activeDisclosureListId
  };
  /**
   * To identify if focus is coming from "shift+tab" checking condition that
   * if container focusCount is one and focus is coming to the right section.
   * If "shift+tab" is pressed then checking below steps:
   * > Checking if this disclosureList is expanded if yes
   *    then focusing last focusable element from this disclosureList
   * > if no then finding title/header element for this disclosureList
   *    and setting focus to that element.
   */

  const handleFocus = () => {
    if (containerRef.current.focusCount === 1) {
      const focusableEls = getFocusableElements(leftColumnRef.current, LEFT_SECTION_FOCUSABLE_SELECTORS);
      const lastFocusableEl = focusableEls[focusableEls.length - 1];
      const closestContainer = lastFocusableEl.closest(`section div.pnr-section__body`);

      if (closestContainer && closestContainer.contains(lastFocusableEl) && closestContainer.classList.contains(`pnr-expandable--expanded`)) {
        focusableEls[focusableEls.length - 1].focus();
      } else {
        var _closestContainer$pre;

        const prevFocusableEl = (_closestContainer$pre = closestContainer.previousElementSibling) === null || _closestContainer$pre === void 0 ? void 0 : _closestContainer$pre.querySelector(`button`);
        prevFocusableEl.focus();
      }
    }
  };
  /**
   * Based on the flags set in "focusout" event increasing the container focusCount by 1
   * This is to identify focus is coming for the first time in container.
   * otherwise assigning 0 to focusCount.
   */


  const handleContainerFocus = () => {
    const {
      shouldFocusDisclosureList
    } = containerRef.current;

    if (shouldFocusDisclosureList && !containerRef.current.focusCount) {
      containerRef.current.focusCount++;
      containerRef.current.shouldFocusDisclosureList = false;
    } else {
      containerRef.current.focusCount = 0;
    }
  };
  /**
   * Resetting flags for mousedown when focus comes to the container
   * When mousedown event is fired then want to execute default behavior
   * instead of set focus to the LeftSection lastItem when shift + tab is pressed.
   */


  const handleContainerMouseDown = () => {
    containerRef.current.shouldFocusDisclosureList = false;
    containerRef.current.focusCount = 0;
  };
  /**
   * Setting flags when focus moving out from the container.
   * This flag will be used to identify when container will get focus
   * from "shift + tab"/ OR mousedown.
   * @param {Object} event
   */


  const handleContainerFocusOut = event => {
    if (containerRef.current && !containerRef.current.contains(event.relatedTarget)) {
      containerRef.current.shouldFocusDisclosureList = true;
      containerRef.current.focusCount = 0;
    }
  };

  useEffect(() => {
    containerRef.current.focusCount = 0;
    const disclosureListContainerRef = containerRef.current;
    containerRef.current.addEventListener("mousedown", handleContainerMouseDown);
    containerRef.current.addEventListener("focus", handleContainerFocus, true);
    containerRef.current.addEventListener("focusout", handleContainerFocusOut);
    return () => {
      disclosureListContainerRef.removeEventListener("mousedown", handleContainerMouseDown);
      disclosureListContainerRef.removeEventListener("focus", handleContainerFocus);
      disclosureListContainerRef.removeEventListener("focusout", handleContainerFocusOut);
    };
  }, []);
  /**
   * Set height for Disclosure Lists div for for scrolling overflow.
   */

  useLayoutEffect(() => {
    const headerHeight = headerRef.current.offsetHeight;
    const leftColumnHeight = leftColumnRef.current.offsetHeight - DISCLOSURE_LISTS_SPACER;
    setDisclosureListsHeight(leftColumnHeight - headerHeight);
  }, []);
  return _jsx(Container, {
    className: "pnr-disclosure-list-container",
    "aria-label": ariaLabel,
    css: cssRoot,
    header: title,
    floatingHeader: true,
    height: height,
    width: width,
    spacing: spacing,
    noPadding: true,
    children: _jsxs("div", {
      ref: containerRef,
      css: cssInnerContainer,
      children: [_jsxs("div", {
        ref: leftColumnRef,
        css: cssLeftColumn,
        "data-testid": "left-column",
        children: [_jsx("div", {
          ref: headerRef,
          css: cssHeader,
          children: header
        }), _jsx("div", {
          css: cssDisclosureLists,
          "data-testid": "disclosure-lists",
          tabIndex: -1,
          children: _jsx(DisclosureListContainerContext.Provider, {
            value: ctx,
            children: _jsx(EnhancedDisclosureLists, {
              children: children
            })
          })
        })]
      }), _jsx("div", {
        css: cssRightColumn,
        children: _jsx("section", {
          className: "pnr-disclosure-list-container__content",
          tabIndex: 0,
          css: cssContentArea,
          role: "dialog",
          id: contentContainerId,
          "aria-label": contentAriaLabel,
          onFocus: handleFocus,
          children: content
        })
      })]
    })
  });
}

DisclosureListContainer.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Array of DisclosureLists */
  children: PropTypes.node.isRequired,
  "aria-label": requiredIf(PropTypes.string, (props, propName) => !props.header && !props["aria-labelledby"] && !props[propName], "when 'header' is not provided."),

  /** Header area */
  header: PropTypes.node,

  /** The height of the DisclosureListContainer. */
  height: PropTypes.string,

  /** The width of the left column of the DisclosureListContainer. */
  leftColumnWidth: PropTypes.string,

  /**
   * Custom spacing for the component. See the
   * "Customization -> Spacing" section.
   */
  spacing: PropTypes.oneOfType([PropTypes.number, PropTypes.shape()]),

  /** Title of DisclosureListContainer */
  title: PropTypes.node,

  /** The width of the DisclosureListContainer. */
  width: PropTypes.string
} : {};
DisclosureListContainer.defaultProps = {
  "aria-label": null,
  title: null,
  header: null,
  height: "600px",
  leftColumnWidth: "400px",
  spacing: {},
  width: "auto"
};
export default DisclosureListContainer;